import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Card,
  Divider,
  Layout,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Tree,
  Typography
} from "antd";
import { HeartFilled, TrophyOutlined } from "@ant-design/icons";

const areaPositions = {
  '항구':     { left: 61.81, top: 89.15 },
  '창고':     { left: 41.94, top: 89.15 },
  '연못':     { left: 70.64, top: 44.49 },
  '개울':     { left: 83.89, top: 36.72 },
  '모래사장':  { left: 13.25, top: 60.02 },
  '고급 주택가': { left: 24.72, top: 77.50 },
  '골목길':   { left: 58.06, top: 5.65 },
  '주유소':   { left: 35.32, top: 5.65 },
  '호텔':     { left: 13.25, top: 40.60 },
  '경찰서':   { left: 64.02, top: 21.18 },
  '소방서':   { left: 52.98, top: 34.78 },
  '병원':     { left: 88.30, top: 56.14 },
  '절':       { left: 83.89, top: 21.18 },
  '양궁장':   { left: 20.31, top: 21.18 },
  '묘지':     { left: 65.34, top: 57.50 },
  '숲':       { left: 40.40, top: 58.66 },
  '공장':     { left: 83.89, top: 75.55 },
  '성당':     { left: 56.73, top: 71.67 },
  '학교':     { left: 35.32, top: 33.61 },
};

const RouteMap = ({ paths, remoteTransferItems }) => {
  if (!paths || paths.length === 0) return null;

  const pathSet = new Set(paths);
  const points = paths
    .map(name => ({ ...areaPositions[name], name }))
    .filter(p => p.left != null);

  if (points.length === 0) return null;

  // 원격 전송 아이템의 드랍 지역 집계: areaName → [itemName, ...]
  const remoteAreaMap = {};
  (remoteTransferItems || []).forEach(item => {
    (item.spawnAreas || []).forEach(area => {
      if (!remoteAreaMap[area]) remoteAreaMap[area] = [];
      remoteAreaMap[area].push(item.name);
    });
  });

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '600px', margin: '0 auto' }}>
      <img
        src="/img/map_v2.png"
        alt="루미아 섬"
        style={{ width: '100%', display: 'block', borderRadius: '8px' }}
      />
      <svg
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        {points.map((point, idx) => {
          if (idx === 0) return null;
          const prev = points[idx - 1];
          return (
            <line
              key={`line-${idx}`}
              x1={prev.left} y1={prev.top}
              x2={point.left} y2={point.top}
              stroke="#faad14"
              strokeWidth="0.5"
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      {/* 원격 전송 드랍 지역 마커 */}
      {Object.entries(remoteAreaMap).map(([areaName, itemNames]) => {
        const pos = areaPositions[areaName];
        if (!pos) return null;
        return (
          <Tooltip
            key={`remote-${areaName}`}
            title={`원격: ${itemNames.join(', ')}`}
            color="#ffffff"
            overlayInnerStyle={{ color: '#000' }}
          >
            <div style={{
              position: 'absolute',
              left: `${pos.left + 2.5}%`,
              top: `${pos.top - 1}%`,
              transform: 'translate(-50%, -50%)',
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              backgroundColor: '#722ed1',
              border: '2px solid #fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: '10px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
              zIndex: 2,
              cursor: 'pointer',
            }}>
              T
            </div>
          </Tooltip>
        );
      })}
      {/* 모든 지역 이름 표시 */}
      {Object.entries(areaPositions).map(([name, pos]) => {
        const isOnRoute = pathSet.has(name);
        return (
          <div
            key={`label-${name}`}
            style={{
              position: 'absolute',
              left: `${pos.left}%`,
              top: `${pos.top + 1.5}%`,
              transform: 'translateX(-50%)',
              fontSize: '10px',
              fontWeight: isOnRoute ? 'bold' : 'normal',
              color: isOnRoute ? '#fff' : 'rgba(255,255,255,0.7)',
              backgroundColor: 'rgba(0,0,0,0.45)',
              padding: '1px 4px',
              borderRadius: '3px',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            {name}
          </div>
        );
      })}
      {/* 동선 번호 마커 */}
      {points.map((point, idx) => (
        <div
          key={idx}
          style={{
            position: 'absolute',
            left: `${point.left}%`,
            top: `${point.top}%`,
            transform: 'translate(-50%, -50%)',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: idx === 0 ? '#52c41a' : idx === points.length - 1 ? '#f5222d' : '#faad14',
            border: '2px solid #fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '12px',
            fontWeight: 'bold',
            boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
            zIndex: 1,
          }}
        >
          {idx + 1}
        </div>
      ))}
    </div>
  );
};

const itemGradeColors = {
  'Common': '#9d9d9d',
  'Uncommon': '#2ecc71',
  'Rare': '#3498db',
  'Epic': '#9b59b6',
  'Legend': '#e67e22',
  'Mythic': '#e74c3c',
};

const gradeDisplayMap = {
  'Common': '일반',
  'Uncommon': '고급',
  'Rare': '희귀',
  'Epic': '영웅',
  'Legend': '전설',
  'Mythic': '신화',
};

const getItemGradeColor = (grade) => itemGradeColors[grade] || '#d9d9d9';

const renderItemIcon = (code, name, grade, size = 50, materialCode = null) => {
  const color = getItemGradeColor(grade);
  return (
    <Tooltip title={`${name} (${gradeDisplayMap[grade] || grade})`} color="#ffffff" overlayInnerStyle={{ color: '#000000' }}>
      <div style={{
        position: 'relative',
        width: `${size}px`,
        height: `${size}px`,
        border: `2px solid ${color}`,
        borderRadius: '4px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(to bottom, ${color}40 0%, ${color}10 50%, #2d2d44 100%)`,
      }}>
        <img
          src={`/img/item/${code}.png`}
          alt={name}
          style={{ width: `${size - 5}px`, height: `${size - 5}px`, objectFit: 'contain' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        {materialCode && (grade === 'Legend' || grade === 'Mythic') && (
          <img
            src={`/img/item/${materialCode}.png`}
            alt=""
            style={{
              position: 'absolute',
              bottom: '-4px',
              right: '-4px',
              width: `${Math.round(size * 0.42)}px`,
              height: `${Math.round(size * 0.42)}px`,
              borderRadius: '50%',
              backgroundColor: '#2d2d44',
              padding: '1px',
              border: '1px solid #555',
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
      </div>
    </Tooltip>
  );
};

const buildTreeData = (itemTree) => {
  if (!itemTree) return [];
  return itemTree.map((item, idx) => ({
    key: `${item.code}-${idx}`,
    title: (
      <Space>
        {renderItemIcon(item.code, item.name, item.grade, 36)}
        <span style={{ color: getItemGradeColor(item.grade), fontWeight: 'bold' }}>{item.name}</span>
      </Space>
    ),
    children: item.materials && item.materials.length > 0 ? buildTreeData(item.materials) : undefined
  }));
};

const getAllTreeKeys = (treeData) => {
  const keys = [];
  const traverse = (nodes) => {
    if (!nodes) return;
    nodes.forEach(node => {
      keys.push(node.key);
      if (node.children) traverse(node.children);
    });
  };
  traverse(treeData);
  return keys;
};

export default function RouteDetail() {
  const [searchParams] = useSearchParams();
  const routeId = searchParams.get('routeId');

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedItemIdx, setSelectedItemIdx] = useState(null);

  useEffect(() => {
    if (!routeId) return;

    setLoading(true);
    axios.get("/api/external/bser/route-detail", {
      params: { routeId }
    })
      .then(r => r.data)
      .then(data => setData(data))
      .finally(() => setLoading(false));
  }, [routeId]);

  if (!routeId) {
    return (
      <Layout style={{ background: '#ffffff' }}>
        <Layout.Content style={{ padding: '20px' }}>
          <Typography.Text>루트 ID가 없습니다. 캐릭터 상세 페이지에서 루트를 선택해주세요.</Typography.Text>
        </Layout.Content>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout style={{ background: '#ffffff' }}>
        <Layout.Content style={{ padding: '20px', textAlign: 'center', marginTop: '100px' }}>
          <Spin size="large" />
        </Layout.Content>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout style={{ background: '#ffffff' }}>
        <Layout.Content style={{ padding: '20px' }}>
          <Typography.Text>데이터를 불러올 수 없습니다.</Typography.Text>
        </Layout.Content>
      </Layout>
    );
  }

  const skillLetters = data.skillPath ? data.skillPath.split(',') : [];

  const skillColorMap = { 'q': '#3498db', 'w': '#2ecc71', 'e': '#e74c3c', 'r': '#f39c12', 't': '#9b59b6' };

  const remoteCodes = new Set((data.remoteTransferItems || []).map(item => item.code));
  const baseCodes = new Set((data.baseMaterials || []).map(item => item.code));
  const baseMaterialData = [
    ...(data.baseMaterials || []).map(item => ({ ...item, _isRemote: remoteCodes.has(item.code) })),
    ...(data.remoteTransferItems || []).filter(item => !baseCodes.has(item.code)).map(item => ({ ...item, _isRemote: true })),
  ];

  const baseMaterialColumns = [
    {
      title: '아이템',
      align: 'center',
      width: 70,
      render: (item) => renderItemIcon(item.code, item.name, item.grade, 40)
    },
    {
      title: '이름',
      dataIndex: 'name',
      align: 'center',
      width: 120,
      render: (name, record) => (
        <Space size={4}>
          <span>{name}</span>
          {record._isRemote && <Tag color="magenta" style={{ margin: 0 }}>원격</Tag>}
        </Space>
      )
    },
    {
      title: '필요 개수',
      dataIndex: 'count',
      align: 'center',
      width: 90,
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a.count || 0) - (b.count || 0),
      render: (count) => count ? <Typography.Text strong>{count}</Typography.Text> : <Typography.Text type="secondary">-</Typography.Text>
    },
    {
      title: '드랍 위치',
      dataIndex: 'spawnAreas',
      align: 'center',
      render: (spawnAreas) => spawnAreas && spawnAreas.length > 0
        ? <Space wrap size={4}>{spawnAreas.map((area, idx) => {
            const isOnRoute = data.paths?.includes(area);
            return <Tag key={idx} color={isOnRoute ? 'blue' : 'default'} style={isOnRoute ? { fontWeight: 'bold' } : {}}>{area}</Tag>;
          })}</Space>
        : <Typography.Text type="secondary">-</Typography.Text>
    }
  ];

  return (
    <Layout style={{ background: '#ffffff' }}>
      <Layout.Content style={{ padding: '20px' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* 헤더: 루트 기본 정보 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
            <Space direction="vertical" size="small">
              <Space align="center">
                <img
                  src={`/img/character/${data.characterName}.png`}
                  alt={data.characterName}
                  style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#d6d6d6' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <Typography.Title level={4} style={{ margin: 0 }}>
                  {data.characterName} [{data.weaponType}]
                </Typography.Title>
              </Space>
              <Typography.Text type="secondary">
                루트 ID: {data.routeId} | 작성자: {data.userNickname} | {data.title}
              </Typography.Text>
            </Space>
            <Space size="large">
              <Statistic
                title="승률"
                value={data.winRate}
                precision={2}
                suffix="%"
                prefix={<TrophyOutlined />}
              />
              <Statistic
                title="좋아요"
                value={data.like}
                prefix={<HeartFilled style={{ color: '#e74c3c' }} />}
              />
            </Space>
          </div>

          {/* 1행: 전술/특성/동선 + 스킬 트리 */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <Card size="small" style={{ height: '100%' }}>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <div>
                    <Typography.Text strong>전술 스킬: </Typography.Text>
                    <Tag color="blue">{data.tacticalSkill}</Tag>
                  </div>
                  <div>
                    <Typography.Text strong>특성: </Typography.Text>
                    <Space wrap>
                      {data.traits?.map((trait, idx) => (
                        <Tag key={idx} color="purple">{trait}</Tag>
                      ))}
                    </Space>
                  </div>
                  <div>
                    <Typography.Text strong>동선: </Typography.Text>
                    <Space wrap>
                      {data.paths?.map((path, idx) => (
                        <span key={idx}>
                          {idx > 0 && <span style={{ margin: '0 4px', color: '#999' }}>&rarr;</span>}
                          <Tag color="green">{path}</Tag>
                        </span>
                      ))}
                    </Space>
                  </div>
                </Space>
              </Card>
            </div>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <Card size="small" style={{ height: '100%' }}>
                <Typography.Text strong>스킬 트리</Typography.Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
                  {skillLetters.map((skill, idx) => (
                    <Tooltip key={idx} title={`레벨 ${idx + 1}`}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: skillColorMap[skill.toLowerCase()] || '#666',
                        color: '#fff',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        textTransform: 'uppercase'
                      }}>
                        {skill.toUpperCase()}
                      </div>
                    </Tooltip>
                  ))}
                </div>
              </Card>
            </div>
          </div>

          {/* 2행: 동선 맵 + 완성 장비 */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <Typography.Title level={5}>동선 맵</Typography.Title>
              <RouteMap paths={data.paths} remoteTransferItems={data.remoteTransferItems} />
            </div>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <Typography.Title level={5}>완성 장비</Typography.Title>
              <Space wrap size="middle">
                {data.weaponItems?.map((item, idx) => (
                  <div
                    key={idx}
                    onClick={() => setSelectedItemIdx(selectedItemIdx === idx ? null : idx)}
                    style={{
                      cursor: 'pointer',
                      border: selectedItemIdx === idx ? '2px solid #1890ff' : '2px solid transparent',
                      borderRadius: '8px',
                      padding: '4px',
                    }}
                  >
                    <Space direction="vertical" align="center" size={4}>
                      {renderItemIcon(item.code, item.name, item.grade, 60)}
                      <Typography.Text style={{ fontSize: '12px', color: getItemGradeColor(item.grade) }}>
                        {item.name}
                      </Typography.Text>
                    </Space>
                  </div>
                ))}
              </Space>
              {selectedItemIdx !== null && data.weaponItemTrees?.[selectedItemIdx] && (() => {
                const tree = data.weaponItemTrees[selectedItemIdx];
                const treeData = buildTreeData([tree]);
                const allKeys = getAllTreeKeys(treeData);
                return (
                  <Card
                    size="small"
                    style={{ marginTop: '12px' }}
                    title={
                      <Space>
                        {renderItemIcon(tree.code, tree.name, tree.grade, 30)}
                        <span style={{ color: getItemGradeColor(tree.grade) }}>{tree.name}</span>
                      </Space>
                    }
                  >
                    <Tree
                      treeData={treeData}
                      defaultExpandAll
                      expandedKeys={allKeys}
                      selectable={false}
                      showLine
                    />
                  </Card>
                );
              })()}
            </div>
          </div>

          <Divider />

          {/* 기본 재료 */}
          <div>
            <Typography.Title level={5}>기본 재료 목록</Typography.Title>
            <Table
              pagination={false}
              dataSource={baseMaterialData}
              columns={baseMaterialColumns}
              rowKey={(record, index) => `${record.code}-${index}`}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </div>

          {/* 후반 교체 아이템 */}
          {data.lateGameItems && Object.keys(data.lateGameItems).length > 0 && (
            <div>
              <Typography.Title level={5}>후반 교체 아이템</Typography.Title>
              {Object.entries(data.lateGameItems).map(([slot, items], slotIdx) => (
                <div key={slot}>
                  {slotIdx > 0 && <Divider style={{ margin: '12px 0' }} />}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                    {items.map((item, idx) => (
                      <div key={idx} style={{ width: '70px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        {renderItemIcon(item.code, item.name, item.grade, 50, item.materialCode)}
                        <Typography.Text style={{ fontSize: '11px', color: getItemGradeColor(item.grade), textAlign: 'center' }}>
                          {item.name}
                        </Typography.Text>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Space>
      </Layout.Content>
    </Layout>
  );
}
